import { useEffect, useRef, useState } from 'react';
import { CircleCheckBig, CircleX, Loader2 } from 'lucide-react';
import { FullscreenDialog } from '@/components/ui/fullscreen-dialog';
import { Button } from '@/components/ui/button';
import { DisabledReasonTooltip } from '@/components/ui/disabled-reason-tooltip';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
} from '@/components/ui/styled-table';
import { Checkbox } from '@/components/ui/checkbox';
import { useAccountProposalSignature } from '@/hooks/account/use-account-proposal-signature';
import { useExecuteAccountProposal } from '@/hooks/account/use-execute-account-proposal';
import {
  AccountProposalsData,
  type ProposalWithSignatures,
} from '@/hooks/account/use-account-proposals';
import { AccountProposalRow } from './account-proposal-row';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { startProposalBatchExecution } from '@/components/execution/start-proposal-batch-execution';
import { useProposalExecutionState } from '@/components/execution/use-proposal-execution-state';
import { ExecutionInProgressButton } from '@/components/execution/execution-in-progress-button';
import { usePendingAccountProposalsForPolling } from '@/hooks/account/use-pending-account-proposals-for-polling';
import { useSelectedInvalidProposal } from '@/hooks/use-selected-invalid-proposal';
import { useSelectedProposalExecutionState } from '@/hooks/use-selected-proposal-execution-state';
import { KeyedMutator } from 'swr';
import type { ProposalExecutionCandidate } from '@/utils/SignatureCountsHelpers';
import {
  OrgOperationDecision,
  type ExecuteAction,
} from '@mls-wallet/shared-types';

interface ReviewAccountCreationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposals: ProposalWithSignatures[];
  onApplied: () => void;
  onAccountsChanged: () => void;
  isProposalStateAvailable: boolean;
  onProposalStateUnavailable: () => void;
  refreshProposals: KeyedMutator<AccountProposalsData>;
}

function toAccountExecutionCandidate(
  proposal: ProposalWithSignatures
): ProposalExecutionCandidate {
  return {
    id: proposal.proposal.id,
    status: proposal.proposal.status,
    approvals: proposal.approvals,
    rejections: proposal.rejections,
    requiredApprovals: proposal.requiredApprovals,
    requiredNetworkIds: proposal.proposal.networkIds,
  };
}

export function ReviewAccountCreationDialog({
  open,
  onOpenChange,
  proposals,
  onApplied,
  onAccountsChanged,
  isProposalStateAvailable,
  onProposalStateUnavailable,
  refreshProposals,
}: ReviewAccountCreationDialogProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [executionProposalIds, setExecutionProposalIds] = useState<string[]>(
    []
  );
  const hasObservedExecutionRef = useRef(false);
  const { createSignature, isLoading: isSignatureLoading } =
    useAccountProposalSignature();
  const { executeProposal, isLoading: isExecuteLoading } =
    useExecuteAccountProposal();
  const isLoading = isSignatureLoading || isExecuteLoading;

  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();

  const allSelected =
    selectedIds.length === proposals.length && proposals.length > 0;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) setSelectedIds(proposals.map((p) => p.proposal.id));
    else setSelectedIds([]);
  };

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const { selectedHasInvalidProposal, approveDisabledReason } =
    useSelectedInvalidProposal(proposals, selectedIds);

  /**
   * Create pending approval rows for the selected proposals before device
   * signing begins. Invalid proposals remain rejectable, but cannot enter the
   * approval flow.
   */
  const handleApproveSelected = async () => {
    // Approval signatures authorize execution, so reject-only proposals must
    // be stopped before any pending approval rows are created.
    if (selectedIds.length === 0 || selectedHasInvalidProposal) return;

    try {
      // Persist every pending row first so the signing modal can monitor the
      // whole selection as one user action.
      await Promise.all(
        selectedIds.map((id) =>
          createSignature({
            proposalId: id,
            decision: OrgOperationDecision.APPROVE,
          })
        )
      );
      // Device signatures do not exist yet. Refreshing exposes the pending rows
      // to the modal, which can then detect either completion or cancellation.
      await refreshProposals();
      setPendingSignableIds(selectedIds);
      setPendingDecision(OrgOperationDecision.APPROVE);
      setIsPendingModalOpen(true);
      setSelectedIds([]);
    } catch (error) {
      console.error('Failed to approve proposals', error);
      resetPendingState();
      // TODO: Implement toast error to show user message based on what the hook responds with.
      // https://linear.app/onchainden/issue/DEN-60/toast-errors
    }
  };

  /**
   * Create pending rejection rows for the selected proposals. Rejection stays
   * available for invalid proposals so administrators can clear them safely.
   */
  const handleRejectSelected = async () => {
    const toReject = selectedIds.slice();
    if (toReject.length === 0) return;

    try {
      // Persist every pending row first so the signing modal can monitor the
      // whole selection as one user action.
      await Promise.all(
        toReject.map((id) =>
          createSignature({
            proposalId: id,
            decision: OrgOperationDecision.REJECT,
          })
        )
      );
      // Device signatures do not exist yet. Refreshing exposes the pending rows
      // to the modal, which can then detect either completion or cancellation.
      await refreshProposals();
      setPendingSignableIds(toReject);
      setPendingDecision(OrgOperationDecision.REJECT);
      setIsPendingModalOpen(true);
      setSelectedIds([]);
    } catch (error) {
      console.error('Failed to reject proposals', error);
      resetPendingState();
      // TODO: Implement toast error to show user message based on what the hook responds with.
      // https://linear.app/onchainden/issue/DEN-60/toast-errors
    }
  };

  // Polling can change readiness after rows are selected. Keep the launch
  // all-or-nothing so one stale or under-signed proposal blocks the batch.
  const {
    selectedProposalIds,
    canExecuteApproval: canExecuteSelected,
    canExecuteRejection: canExecuteRejectionSelected,
  } = useSelectedProposalExecutionState(
    proposals,
    selectedIds,
    toAccountExecutionCandidate
  );
  const proposalIds =
    executionProposalIds.length > 0 ? executionProposalIds : selectedIds;
  const trackedProposalIds = new Set(proposalIds);
  const isServerExecuting = proposals.some(
    ({ proposal, isExecuting }) =>
      trackedProposalIds.has(proposal.id) && isExecuting
  );
  const { isExecuting: isProposalExecuting, startExecution } =
    useProposalExecutionState({
      proposalId: proposalIds,
      isServerExecuting: isServerExecuting || !isProposalStateAvailable,
    });

  useEffect(() => {
    // Retain the batch IDs until an observed local or server execution becomes terminal.
    if (executionProposalIds.length === 0) return;
    // A conflict invalidates cached idle state. Only a usable proposal response
    // can confirm that the competing execution has reached a terminal state.
    if (!isProposalStateAvailable) return;
    if (isProposalExecuting) {
      hasObservedExecutionRef.current = true;
      return;
    }
    if (!hasObservedExecutionRef.current) return;
    hasObservedExecutionRef.current = false;
    setExecutionProposalIds([]);
  }, [executionProposalIds, isProposalExecuting, isProposalStateAvailable]);

  const approveButtonDisabled = canExecuteSelected
    ? isLoading || selectedHasInvalidProposal
    : selectedIds.length === 0 || isLoading || selectedHasInvalidProposal;

  /**
   * Hand the validated batch to the global execution tray. The tray outlives
   * this dialog and reports each proposal separately, so navigation cannot hide
   * a remaining execution or a partial failure.
   */
  const handleExecute = (action: ExecuteAction) => {
    // Case: return when the batch is empty or any selected proposal is already executing.
    if (selectedProposalIds.length === 0 || isProposalExecuting) return;

    // Readiness comes from polled data, so re-check the chosen action instead of
    // trusting the button state from an earlier render.
    if (action === 'APPROVE' && !canExecuteSelected) return;
    // Invalid account proposals are reject-only; their approval path must never
    // reach the asynchronous executor.
    if (action === 'APPROVE' && selectedHasInvalidProposal) return;
    if (action === 'REJECT' && !canExecuteRejectionSelected) return;

    // Clear only local dialog state. The helper registers the full batch
    // synchronously before this dialog can close or unmount.
    setExecutionProposalIds(selectedProposalIds);
    hasObservedExecutionRef.current = false;
    setSelectedIds([]);
    startProposalBatchExecution({
      proposalIds: selectedProposalIds,
      action,
      proposalType: 'account',
      startExecution,
      executeProposal,
      onAlreadyExecuting: async () => {
        // The conflict proves the cached idle response is stale. Undefined or
        // failed refreshes leave the batch blocked until a later poll succeeds.
        hasObservedExecutionRef.current = true;
        onProposalStateUnavailable();
        await refreshProposals();
      },
      onSuccess: async () => {
        // Refresh after each success so an applied proposal remains visible even
        // when another proposal in the same batch fails.
        onApplied();
        onAccountsChanged();
        await refreshProposals();
      },
    });
  };

  return (
    <FullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Review account proposals (${proposals.length})`}
      noPadding
      bodyContainerClassName="bg-muted"
    >
      <div className="p-6">
        {proposals.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 text-center">
            <p className="text-sm text-muted-foreground">
              No proposed changes are waiting for review.
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <>
            <div className="flex justify-end gap-3 mb-6">
              {isProposalExecuting ? (
                <ExecutionInProgressButton />
              ) : (
                <>
                  <Button
                    size="default"
                    onClick={
                      canExecuteRejectionSelected
                        ? () => handleExecute('REJECT')
                        : handleRejectSelected
                    }
                    disabled={
                      canExecuteRejectionSelected
                        ? isLoading
                        : selectedIds.length === 0 || isLoading
                    }
                    className={`bg-rose-700 text-orange-100 text-shadow-sm border-orange-300 hover:bg-red-600 text-sm font-medium ${
                      (
                        canExecuteRejectionSelected
                          ? isLoading
                          : selectedIds.length === 0 || isLoading
                      )
                        ? 'saturate-[.2] !opacity-25'
                        : ''
                    }`}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CircleX className="h-4 w-4 mr-1" />
                    )}
                    {canExecuteRejectionSelected
                      ? selectedIds.length === 1
                        ? 'Execute rejection'
                        : `Execute rejections (${selectedIds.length})`
                      : `Reject selected (${selectedIds.length})`}
                  </Button>

                  <DisabledReasonTooltip reason={approveDisabledReason}>
                    <Button
                      size="default"
                      onClick={
                        canExecuteSelected
                          ? () => handleExecute('APPROVE')
                          : handleApproveSelected
                      }
                      disabled={approveButtonDisabled}
                      className={`bg-green-700 text-yellow-100 text-shadow-sm border-lime-300 hover:bg-green-600 text-sm font-medium ${
                        approveButtonDisabled ? 'saturate-[.2] !opacity-25' : ''
                      }`}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <CircleCheckBig className="h-4 w-4 mr-1" />
                      )}
                      {canExecuteSelected
                        ? selectedIds.length === 1
                          ? 'Execute'
                          : `Execute selected (${selectedIds.length})`
                        : `Approve selected (${selectedIds.length})`}
                    </Button>
                  </DisabledReasonTooltip>
                </>
              )}
            </div>

            <StyledTableWrapper>
              <StyledTable>
                <StyledTableHeader>
                  <StyledTableRow>
                    <StyledTableHead className="w-12">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={(checked) =>
                          toggleSelectAll(!!checked)
                        }
                        aria-label="Select all"
                      />
                    </StyledTableHead>
                    <StyledTableHead>Account Name</StyledTableHead>
                    <StyledTableHead>Blockchain Networks</StyledTableHead>
                    <StyledTableHead className="w-40"></StyledTableHead>
                    <StyledTableHead className="w-40"></StyledTableHead>
                  </StyledTableRow>
                </StyledTableHeader>
                <StyledTableBody>
                  {proposals.map((p) => (
                    <AccountProposalRow
                      key={p.proposal.id}
                      proposal={p}
                      isSelected={selectedIds.includes(p.proposal.id)}
                      onSelectChange={toggleSelect}
                    />
                  ))}
                </StyledTableBody>
              </StyledTable>
            </StyledTableWrapper>
          </>
        )}
      </div>

      <PendingSignaturesModal
        open={isPendingModalOpen}
        resetPendingState={resetPendingState}
        refreshSignables={refreshProposals}
        pendingSignableIds={pendingSignableIds}
        polledSignables={proposals.map((p) => ({
          id: p.proposal.id,
          approvals: p.approvals,
          rejections: p.rejections,
          requiredApprovals: p.requiredApprovals,
          initiatorSignature: undefined,
        }))}
        pendingDecision={pendingDecision}
        hookFunctionToPoll={usePendingAccountProposalsForPolling}
        isTransaction={false}
      />
    </FullscreenDialog>
  );
}
